import { useContext, useMemo } from "react";
import { Context as IntlContext } from "context/Intl";
import type { FilterOption } from "components/ListPage/filters";
import type { AdCategoryReference } from "lib/api/reference/adCategory";
import type { ProviderCategoryOptions } from "./types";

export const useProviderCategoryOptions = (
    categories: AdCategoryReference[],
    selectedCategories: string[],
    selectedSubcategories: string[]
): ProviderCategoryOptions => {
    const intl = useContext(IntlContext);
    const categoryOptions = useMemo<FilterOption[]>(
        () =>
            categories.map((category) => ({
                value: category.value,
                label: String(intl.formatMessage(`category.${category.value.toLowerCase()}`)),
            })),
        [categories, intl]
    );
    const selectedCategorySet = useMemo(() => new Set(selectedCategories), [selectedCategories]);
    const subcategoryOptions = useMemo<FilterOption[]>(
        () =>
            categories
                .filter((category) => selectedCategorySet.size === 0 || selectedCategorySet.has(category.value))
                .flatMap((category) =>
                    category.subcategories.map((subcategory) => ({
                        value: subcategory.value,
                        label: String(
                            intl.formatMessage(
                                `subcategory.${category.value.toLowerCase()}.${subcategory.value.toLowerCase()}.label`
                            )
                        ),
                    }))
                ),
        [categories, intl, selectedCategorySet]
    );
    const displayedSubcategories = useMemo(() => {
        const validSubcategoryValues = new Set(subcategoryOptions.map((subcategory) => subcategory.value));
        return selectedSubcategories.filter((subcategory) => validSubcategoryValues.has(subcategory));
    }, [selectedSubcategories, subcategoryOptions]);

    return {
        categories,
        categoryOptions,
        subcategoryOptions,
        displayedSubcategories,
    };
};
